import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Check, Clock, Ban } from 'lucide-react';
import chatService from '../../services/chatService';
import UserAvatar from '../ui/UserAvatar';
import InviteeSuggestionsDropdown from './InviteeSuggestionsDropdown';
import { isCompleteEmail } from '../chatHelpers';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { rosterFromChannels, filterRoster } from '../../utils/peopleRoster';
import { MAX_INVITEES_PER_CREATE } from '../../services/calendarInvitesService';

/**
 * Invite-people picker for the event editor: a browsable list of the people you
 * already chat with, plus the exact-email lookup kept as the escape hatch for
 * everyone else, chips for the picked users, and the live status of
 * already-sent invitations (organizer's GET sent rows).
 *
 * WHERE THE LIST COMES FROM, AND WHY IT DISCLOSES NOTHING NEW (owner D1/D2,
 * 2026-08-31): the rows come from `chatStore.channels` — the DM, group and
 * channel-room memberships `useChatRealtime()` already holds in memory. They
 * are exactly the people the chat sidebar already shows by name and avatar, so
 * nobody becomes findable who was not findable before. Filtering that list is
 * LOCAL and never becomes a request. Discovery beyond it stays exact-email
 * only, by written server policy ("the directory must not be browsable",
 * `chat.ex`) — a partial query must never leave the machine.
 *
 * Lookup goes through `chatService.searchUsers` rather than the team endpoint:
 * `Teams.search_users` is a case-sensitive `Repo.get_by` that also fails to
 * exclude the caller, so `Alice@Example.com` found nobody. Chat's lowercases
 * both sides and excludes self.
 *
 * SUGGESTIONS LIVE IN A DROPDOWN, COMMITTED STATE DOES NOT (owner,
 * 2026-08-31). The roster and the lookup result are both guesses at who you
 * meant, so they belong to the input and appear only while it is focused. The
 * picked chips, the sent-invitation rows, the cap hint and any error are
 * decisions already taken — they stay inline and always visible, because
 * hiding them would hide what the organizer is about to send.
 *
 * Pure UI: the parent owns `invitees` (new picks) and `sentRows` (existing) —
 * the actual create/PATCH calls happen on Save through the single service.
 * Shared control skins per the round-4 standard (`modal-input`, `cal-btn`).
 */

/** Roster rows shown at once — a long membership must not become a wall. */
const MAX_ROSTER_ROWS = 30;

export default function InviteePicker({ invitees, onChange, sentRows = [], disabled = false }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  // The dropdown. `open` is the ONLY gate on the suggestion sections — there is
  // deliberately no second condition, because a suggestion list that can render
  // for some other reason is a feature that undoes itself while the suite stays
  // green.
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const triggerRef = useRef(null);
  // Measured for the dropdown's position. NOT `triggerRef`: that row also holds
  // the search button, so its box can be taller than the input and the list
  // then hangs visibly below the field it belongs to (owner, 31.08.2026).
  const inputRef = useRef(null);
  const openRef = useRef(false);
  openRef.current = open;
  const listboxId = `invitee-suggestions-${useId()}`;

  // Re-measure, not measure-once. The rect was originally taken only when the
  // list opened, so anything that moved the input afterwards left the dropdown
  // behind at stale coordinates — and adding a chip does exactly that: the
  // editor grows, the centred modal shifts, the input moves, and the list
  // appeared to drift away from the field (owner, 31.08.2026). State is only
  // written when the box actually changed, so this cannot loop.
  const measure = useCallback(() => {
    const r = inputRef.current?.getBoundingClientRect();
    if (!r) return;
    setAnchorRect((prev) =>
      prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.bottom === r.bottom
        ? prev
        : r
    );
  }, []);

  const openList = () => {
    if (disabled) return;
    measure();
    setOpen(true);
  };
  const closeList = () => setOpen(false);

  // Deliberately no dependency list: ANY render can be the one that moved the
  // input (a chip added, an error hint appearing, the cap warning showing), and
  // enumerating those was both fragile and a temporal-dead-zone trap — half of
  // them are declared below this point. `measure` writes state only when the
  // box actually changed, so the extra pass settles immediately.
  useLayoutEffect(() => {
    if (open) measure();
  });

  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, measure]);

  // ESCAPE CLOSES THE DROPDOWN, NOT THE WHOLE EVENT EDITOR.
  //
  // `Modal` listens for Escape on `document` in the CAPTURE phase and calls
  // `stopImmediatePropagation()` (`ui/Modal.js:31-33`), so a React `onKeyDown` on
  // the input can never see the key — it would be a handler that reads correct
  // and does nothing, and the organizer would lose every unsaved edit to a
  // keypress meant to dismiss a suggestion list. (`TimeCombobox.js:183` has
  // exactly that dead handler today.)
  //
  // WINDOW, NOT `document`, AND THE DISTINCTION IS THE WHOLE FIX. Two capture
  // listeners on the SAME node are ordered by registration, which is a race this
  // component loses: `EventEditorModal.js:382` renders the picker behind
  // `!(recurrence || isOverride)`, so toggling Repeats (or switching to a team
  // calendar) REMOUNTS it and re-registers this effect long after `Modal`'s.
  // Modal's handler then runs first and Escape discards the whole event.
  // A `window` listener is ordered by PHASE instead: capture travels
  // window -> document -> ... -> target, so this always precedes `Modal`'s no
  // matter when either registered, and no remount can change that. Do not
  // "simplify" it back to `document`. Reviewer finding B1; `D3b` is the control,
  // and `D3` alone does NOT catch it.
  //
  // Scoped hard: it swallows Escape ONLY while the dropdown is open. Closed, the
  // key falls through and closes the editor exactly as before.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || !openRef.current) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const channels = useChatStore((s) => s.channels);
  const currentUserId = useAuthStore((s) => s.user?.id);

  const roster = useMemo(
    () => rosterFromChannels(channels, { currentUserId }),
    [channels, currentUserId]
  );

  const alreadyListed = (userId) =>
    invitees.some((u) => String(u.id) === String(userId)) ||
    sentRows.some((r) => String(r.user_id) === String(userId));

  // Server cap per create call (backend interp. 4) — guarded HERE too so the
  // organizer hits a visible wall, never a silent truncation (tester N3).
  const atCap = invitees.length >= MAX_INVITEES_PER_CREATE;

  const trimmed = query.trim();
  // THE PRIVACY GATE. A partial query is filtered locally and fires nothing.
  const emailMode = isCompleteEmail(query);

  const runSearch = async (email) => {
    // THE PRIVACY GATE, second copy: the explicit button may not smuggle a
    // partial query past the debounce's `emailMode` check.
    if (!email || !isCompleteEmail(email)) return;
    clearTimeout(debounceRef.current);
    setSearching(true);
    setError(null);
    try {
      const users = await chatService.searchUsers(email);
      setResults(Array.isArray(users) ? users : []);
    } catch (err) {
      setResults([]);
      setError(err?.message || t('calendar.invite.searchFailed'));
    } finally {
      setSearching(false);
    }
  };

  // Debounced lookup, 250 ms, gated on a COMPLETE address (mirrors
  // NewChannelModal). Anything shorter stays on this machine.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!emailMode) {
      setResults([]);
      setSearching(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setResults([]);
    setError(null);
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      chatService
        .searchUsers(query.trim())
        .then((users) => {
          if (cancelled) return;
          setResults(Array.isArray(users) ? users : []);
          setSearching(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setResults([]);
          setError(err?.message || t('calendar.invite.searchFailed'));
          setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, emailMode]);

  const addUser = (user) => {
    if (disabled || alreadyListed(user.id) || atCap) return;
    onChange([...invitees, { id: user.id, name: user.name, email: user.email }]);
    setResults([]);
    setQuery('');
  };

  const rosterMatches = useMemo(
    () => filterRoster(roster, query).slice(0, MAX_ROSTER_ROWS),
    [roster, query]
  );

  // A lookup that returns someone already shown above must not render them a
  // second time — the two sections are one list to the reader.
  //
  // DEDUPE AGAINST THE VISIBLE ROWS, NEVER AGAINST `roster`. Do not "simplify"
  // this to `roster.map(...)`: being IN the roster does not mean being ON the
  // screen. The server matches on exact email equality, so a hit's address
  // always contains the query — but the ROSTER row for that same person may
  // not, because the roster is a snapshot the chat channel list loaded once on
  // auth. A stale address, or a member the serializer gave no email at all
  // (coerced to '' in `peopleRoster.js`), fails `filterRoster` for the very
  // address you just typed. A full-roster dedupe would then drop them from the
  // search section while the roster section is also not rendering them: the
  // person is on screen NOWHERE, unreachable, with a green suite.
  // `calendarInviteeRoster.test.js` B1d is the control for exactly that.
  const visibleRosterIds = new Set(rosterMatches.map((u) => String(u.id)));
  const searchMatches = results.filter((u) => !visibleRosterIds.has(String(u.id)));

  // The lookup DID find somebody, and the dedupe above removed them because
  // they are already on screen — typing a full address filters the roster down
  // to exactly that person, so their row is right there. Saying "No Apiboo
  // user with that email." underneath it is a contradiction, and an empty
  // "Search result" heading is barely better. Say nothing: the row above is
  // the answer.
  //
  // This is only safe because `searchMatches` deduped against the VISIBLE rows
  // (see above) — suppressing the section is a claim that the person is
  // already rendered, and only the visible-set dedupe makes that claim true.
  const everyResultAlreadyShown =
    !searching && results.length > 0 && searchMatches.length === 0;

  const statusBadge = (status) => {
    if (status === 'accepted')
      return (
        <span style={{ color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <Check size={11} /> {t('calendar.invite.accepted')}
        </span>
      );
    if (status === 'declined')
      return (
        <span style={{ color: '#e5484d', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <Ban size={11} /> {t('calendar.invite.declined')}
        </span>
      );
    return (
      <span style={{ color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {/* Unknown server statuses still render verbatim (previous behaviour);
            only the one status this client knows about is translated. */}
        <Clock size={11} />{' '}
        {status && status !== 'pending' ? status : t('calendar.invite.pending')}
      </span>
    );
  };

  const personRow = (user, keyPrefix) => {
    const already = alreadyListed(user.id);
    const rowDisabled = disabled || already || atCap;
    return (
      <button
        key={`${keyPrefix}-${user.id}`}
        type="button"
        className="flex items-center"
        role="option"
        aria-selected={false}
        // Options are not in the tab order: the input keeps focus for the whole
        // life of the dropdown (ARIA combobox pattern, and owner decision 3).
        // Without this a Tab would move focus into a row, the focus-out handler
        // would close the list, and the row would vanish under the caret.
        tabIndex={-1}
        disabled={rowDisabled}
        aria-label={t('calendar.invite.addUser', { name: user.name || user.email })}
        onClick={() => addUser(user)}
        style={{
          gap: 8,
          width: '100%',
          padding: '4px 6px',
          border: 'none',
          borderRadius: 8,
          background: 'transparent',
          cursor: rowDisabled ? 'default' : 'pointer',
          opacity: rowDisabled ? 0.6 : 1,
          textAlign: 'left',
          fontSize: 13,
          color: 'inherit',
        }}
      >
        <UserAvatar userId={user.id} name={user.name} size={24} />
        <span style={{ minWidth: 0, overflow: 'hidden' }}>
          <span style={{ display: 'block', textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {user.name || user.email}
          </span>
          <span style={{ display: 'block', fontSize: 11, color: '#9ca3af' }}>
            {already ? t('calendar.invite.alreadyAdded') : user.email}
          </span>
        </span>
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
      <div
        ref={triggerRef}
        data-invitee-dropdown="true"
        style={{ display: 'flex', gap: 6 }}
        // FOCUS-OUT closes. The check is against the TRIGGER only, never against
        // the dropdown: focus is never allowed into the dropdown in the first
        // place (rows are `tabIndex={-1}`, and the list suppresses the default
        // action of `mousedown`), so "focus went anywhere else" is exactly the
        // condition to close on. Widening this to also exempt the dropdown's
        // own nodes would make the `preventDefault` above unnecessary — and an
        // unnecessary guard is one nobody notices deleting.
        onBlur={(e) => {
          if (triggerRef.current?.contains(e.relatedTarget)) return;
          setOpen(false);
        }}
      >
        <input
          className="modal-input"
          type="text"
          role="combobox"
          aria-expanded={open}
          ref={inputRef}
          aria-controls={listboxId}
          aria-autocomplete="list"
          placeholder={t('calendar.invite.searchPlaceholder')}
          aria-label={t('calendar.invite.inviteByEmail')}
          value={query}
          disabled={disabled}
          onFocus={openList}
          // Also on click: Escape closes the list WITHOUT moving focus, so a
          // plain `onFocus` would never fire again and the list could not be
          // reopened without tabbing away first.
          onClick={openList}
          // NOT `openList()` as well, deliberately. The owner's rule is "open on
          // focus", and a keystroke can only reach a focused input — so an open
          // here is unreachable in a browser and does exactly one thing: it lets
          // a test that never focused anything still see the suggestions, which
          // is how this feature would get silently undone.
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            // ENTER NEEDS A QUERY. `filterRoster` returns the roster UNFILTERED
            // for a blank query, so a pick-first here would append whoever
            // sorts first alphabetically — and that chip becomes a real
            // invitation and notification to somebody the organizer never
            // chose. The old picker did nothing on an empty Enter; so does
            // this one. Whitespace counts as empty.
            if (!trimmed) return;
            const first = [...rosterMatches, ...searchMatches].find((u) => !alreadyListed(u.id));
            if (first) addUser(first);
            else if (emailMode) runSearch(trimmed);
            // Non-empty, nothing matched, and not a complete address: Enter
            // deliberately does NOTHING. There is nobody to add, and the only
            // other thing Enter could do is fire a lookup — which for a
            // partial query is exactly what the privacy gate forbids. The
            // `noMatches` / `noRosterHint` copy already explains the state.
          }}
        />
        {/* Enabled through the debounce window on purpose: clicking cancels the
            pending timer and looks the address up now. Never enabled for a
            partial query — that is the privacy gate, not a UX nicety. */}
        <button
          type="button"
          className="cal-btn"
          disabled={disabled || !emailMode}
          // Opens the list too: the button is the one way to start a lookup
          // without the input ever having been focused, and a result rendered
          // into a closed dropdown is a lookup the organizer never sees.
          onClick={() => {
            openList();
            runSearch(trimmed);
          }}
          aria-label={t('calendar.invite.searchUser')}
        >
          <Search size={13} />
        </button>
      </div>

      {error && <span className="cal-editor-hint">{error}</span>}
      {atCap && (
        <span className="cal-editor-hint">
          {t('calendar.invite.tooManyInviteesHint', { count: MAX_INVITEES_PER_CREATE })}
        </span>
      )}

      {/* SUGGESTIONS — and nothing else — live behind `open`. Both sections are
          guesses at who the organizer meant; neither is state they have
          committed to, so neither belongs on screen until they ask. */}
      {open && (
        <InviteeSuggestionsDropdown
          anchorRect={anchorRect}
          id={listboxId}
          ariaLabel={t('calendar.invite.suggestionsLabel')}
          onRequestClose={closeList}
        >
          {roster.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="cal-editor-hint">{t('calendar.invite.rosterHeading')}</span>
              {rosterMatches.length > 0 ? (
                rosterMatches.map((u) => personRow(u, 'roster'))
              ) : (
                <span className="cal-editor-hint">{t('calendar.invite.noMatches')}</span>
              )}
            </div>
          )}
          {roster.length === 0 && (
            <span className="cal-editor-hint">{t('calendar.invite.noRosterHint')}</span>
          )}

          {emailMode && !everyResultAlreadyShown && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="cal-editor-hint">{t('calendar.invite.searchHeading')}</span>
              {searchMatches.map((u) => personRow(u, 'search'))}
              {searchMatches.length === 0 && (
                <span className="cal-editor-hint">
                  {searching ? t('calendar.invite.searching') : t('calendar.invite.noUser')}
                </span>
              )}
            </div>
          )}
        </InviteeSuggestionsDropdown>
      )}

      {/* Chips, not one row per person (owner, 31.08.2026). A list of full-width
          rows pushed the rest of the editor down as soon as you invited a few
          people; chips wrap and keep the block the height of its content. The
          "will be invited" wording moves to each chip's `title` — inside a chip
          it is repeated noise, but it is the only thing distinguishing a
          not-yet-saved pick from a sent invitation, so it is kept, not dropped. */}
      {(sentRows.length > 0 || invitees.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontSize: 12 }}>
          {sentRows.map((row) => (
            <span
              key={`sent-${row.id ?? row.user_id}`}
              className="cal-invitee-chip"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%' }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.user?.name || row.user?.email || t('calendar.invite.userN', { id: row.user_id })}
              </span>
              {statusBadge(row.status)}
            </span>
          ))}
          {invitees.map((user) => (
            <span
              key={user.id}
              className="cal-invitee-chip cal-invitee-chip--pending"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 2, maxWidth: '100%' }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.name || user.email}
              </span>
              <button
                type="button"
                className="cal-btn cal-btn--icon"
                aria-label={t('calendar.invite.removeUser', { name: user.name || user.email })}
                disabled={disabled}
                onClick={() => onChange(invitees.filter((u) => u.id !== user.id))}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <span className="cal-editor-hint">{t('calendar.invite.hint')}</span>
    </div>
  );
}
